import { useEffect, useState } from "react";
import axios from "axios";
import xmlJs from "xml-js";
import { ArrowLeft, Image as ImageIcon, Video, FileText, BarChart3, Download, Sparkles } from "lucide-react";
import "../Styles/Assets.scss";

const Assets = () => {
  const [activeCategory, setActiveCategory] = useState(null); // null | 'logos' | 'videos' | 'leaflets' | 'decks'

  return (
    <div className="apple-assets-wrapper">
      {activeCategory === null ? (
        <div className="apple-assets-grid-view">
          {/* Header Tagline */}
          <div className="apple-assets-header-meta">
            <span className="apple-assets-tag">PARTNER ASSETS</span>
            <h2 className="apple-assets-title">Marketing & Brand Resources</h2>
            <p className="apple-assets-sub">
              Access official iNextLabs logos, videos, brochures, and pitch decks.
            </p>
          </div>

          {/* 2x2 Apple Ceramic Grid */}
          <div className="apple-assets-card-grid">
            <div
              className="apple-asset-nav-card"
              onClick={() => setActiveCategory("logos")}
            >
              <div className="apple-asset-icon-stage">
                <ImageIcon size={26} />
              </div>
              <h3 className="apple-asset-card-title">Logos</h3>
              <p className="apple-asset-card-sub">Official vector & high-res brand marks</p>
              <span className="apple-asset-link">Browse logos →</span>
            </div>

            <div
              className="apple-asset-nav-card"
              onClick={() => setActiveCategory("videos")}
            >
              <div className="apple-asset-icon-stage">
                <Video size={26} />
              </div>
              <h3 className="apple-asset-card-title">Videos</h3>
              <p className="apple-asset-card-sub">Product walkthroughs & demo clips</p>
              <span className="apple-asset-link">Browse videos →</span>
            </div>

            <div
              className="apple-asset-nav-card"
              onClick={() => setActiveCategory("leaflets")}
            >
              <div className="apple-asset-icon-stage">
                <FileText size={26} />
              </div>
              <h3 className="apple-asset-card-title">Brochures</h3>
              <p className="apple-asset-card-sub">Product leaflets & solution briefs</p>
              <span className="apple-asset-link">Browse brochures →</span>
            </div>

            <div
              className="apple-asset-nav-card"
              onClick={() => setActiveCategory("decks")}
            >
              <div className="apple-asset-icon-stage">
                <BarChart3 size={26} />
              </div>
              <h3 className="apple-asset-card-title">Decks</h3>
              <p className="apple-asset-card-sub">Partner sales & pitch presentations</p>
              <span className="apple-asset-link">Browse decks →</span>
            </div>
          </div>
        </div>
      ) : (
        <div className="apple-assets-category-view">
          {/* Top Breadcrumb Navigation */}
          <div className="apple-assets-nav-bar">
            <button
              type="button"
              className="apple-assets-back-pill"
              onClick={() => setActiveCategory(null)}
            >
              <ArrowLeft size={16} />
              <span>Back to Assets</span>
            </button>
            <span className="apple-assets-category-tag">
              {activeCategory.toUpperCase()}
            </span>
          </div>

          {/* Inline Active Asset View */}
          <div className="apple-assets-view-body">
            {activeCategory === "logos" && <Logos />}
            {activeCategory === "videos" && <Videos />}
            {activeCategory === "leaflets" && <Leaflets />}
            {activeCategory === "decks" && <Decks />}
          </div>
        </div>
      )}
    </div>
  );
};

const Logos = () => {
  const [logosData, setlogosData] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get(
          "https://inextlabswebassets.blob.core.windows.net/partners-demo-assets-logos?restype=container&comp=list"
        );
        const xmlData = response.data;
        const jsonData = xmlJs.xml2json(xmlData, { compact: true, spaces: 2 });
        const blobs = JSON.parse(jsonData).EnumerationResults.Blobs.Blob;
        setlogosData(Array.isArray(blobs) ? blobs : [blobs]);
      } catch (error) {
        console.error("Error fetching logos data:", error);
      }
    };

    fetchData();
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const handleDownload = (url, fileName) => {
    axios({
      url: url,
      method: "GET",
      responseType: "blob",
    }).then((response) => {
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
    });
  };

  return (
    <div>
      {logosData ? (
        <div className="apple-assets-list">
          {logosData.map((logo, index) => (
            <div key={index} className="apple-asset-item-card">
              <div className="apple-asset-media-box">
                <img src={logo.Url._text} alt={logo.Name?._text || "Logo"} />
              </div>
              <div className="apple-asset-item-info">
                <h4 className="apple-asset-item-name">{logo.Name._text.split(".")[0]}</h4>
                <p className="apple-asset-item-date">
                  Updated {formatDate(logo.Properties["Last-Modified"]._text)}
                </p>
                <button
                  type="button"
                  className="apple-asset-download-pill"
                  onClick={() =>
                    handleDownload(logo.Url._text, logo.Name._text)
                  }
                >
                  <Download size={15} />
                  <span>Download Asset</span>
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="apple-assets-loading">
          <Sparkles className="spin-icon" size={24} color="#F05742" />
          <span>Loading logos...</span>
        </div>
      )}
    </div>
  );
};

const Videos = () => {
  const [videoData, setVideoData] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get(
          "https://inextlabswebassets.blob.core.windows.net/partners-demo-assets-video?restype=container&comp=list"
        );
        const xmlData = response.data;
        const jsonData = xmlJs.xml2json(xmlData, { compact: true, spaces: 2 });
        const blobs = JSON.parse(jsonData).EnumerationResults.Blobs.Blob;
        setVideoData(Array.isArray(blobs) ? blobs : [blobs]);
      } catch (error) {
        console.error("Error fetching video data:", error);
      }
    };

    fetchData();
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  return (
    <div>
      {videoData ? (
        <div className="apple-assets-list">
          {videoData.map((video, index) => (
            <div key={index} className="apple-asset-item-card">
              <div className="apple-asset-video-box">
                <video className="apple-asset-video-element" controls>
                  <source
                    src={video.Url._text}
                    type={video.Properties["Content-Type"]._text}
                  />
                  Your browser does not support video playback.
                </video>
              </div>
              <div className="apple-asset-item-info">
                <h4 className="apple-asset-item-name">
                  {video.Name._text.split(".")[0]}
                </h4>
                <p className="apple-asset-item-date">
                  Updated {formatDate(video.Properties["Last-Modified"]._text)}
                </p>
                <a
                  href={video.Url._text}
                  download={video.Name._text.split(".")[0]}
                  target="_blank"
                  rel="noreferrer"
                  className="apple-asset-download-pill"
                >
                  <Download size={15} />
                  <span>Download Video</span>
                </a>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="apple-assets-loading">
          <Sparkles className="spin-icon" size={24} color="#F05742" />
          <span>Loading videos...</span>
        </div>
      )}
    </div>
  );
};

const Leaflets = () => {
  const [leafletsData, setLeafletsData] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get(
          "https://inextlabswebassets.blob.core.windows.net/partners-demo-assets-brochures?restype=container&comp=list"
        );
        const xmlData = response.data;
        const jsonData = xmlJs.xml2json(xmlData, { compact: true, spaces: 2 });
        const blobs = JSON.parse(jsonData).EnumerationResults.Blobs.Blob;
        setLeafletsData(Array.isArray(blobs) ? blobs : [blobs]);
      } catch (error) {
        console.error("Error fetching leaflets data:", error);
      }
    };

    fetchData();
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const handleDownload = (url, fileName) => {
    axios({
      url: url,
      method: "GET",
      responseType: "blob",
    }).then((response) => {
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
    });
  };

  return (
    <div>
      {leafletsData ? (
        <div className="apple-assets-list">
          {leafletsData.map((leaflet, index) => (
            <div key={index} className="apple-asset-item-card">
              <div className="apple-asset-media-box pdf-box">
                <FileText size={44} color="#F05742" />
              </div>
              <div className="apple-asset-item-info">
                <h4 className="apple-asset-item-name">
                  {leaflet.Name._text.split(".")[0]}
                </h4>
                <p className="apple-asset-item-date">
                  Updated {formatDate(leaflet.Properties["Last-Modified"]._text)}
                </p>

                <button
                  type="button"
                  className="apple-asset-download-pill"
                  onClick={() =>
                    handleDownload(leaflet.Url._text, leaflet.Name._text)
                  }
                >
                  <Download size={15} />
                  <span>Download PDF</span>
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="apple-assets-loading">
          <Sparkles className="spin-icon" size={24} color="#F05742" />
          <span>Loading brochures...</span>
        </div>
      )}
    </div>
  );
};

const Decks = () => {
  const [decksData, setDecksData] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get(
          "https://inextlabswebassets.blob.core.windows.net/partners-demo-assets-decks?restype=container&comp=list"
        );
        const xmlData = response.data;
        const jsonData = xmlJs.xml2json(xmlData, { compact: true, spaces: 2 });
        const blobs = JSON.parse(jsonData).EnumerationResults.Blobs.Blob;
        setDecksData(Array.isArray(blobs) ? blobs : [blobs]);
      } catch (error) {
        console.error("Error fetching deck data:", error);
      }
    };

    fetchData();
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  return (
    <div>
      {decksData ? (
        <div className="apple-assets-list">
          {decksData.map((deck, index) => (
            <div key={index} className="apple-asset-item-card">
              <div className="apple-asset-media-box ppt-box">
                <BarChart3 size={44} color="#F05742" />
              </div>
              <div className="apple-asset-item-info">
                <h4 className="apple-asset-item-name">{deck.Name._text.split(".")[0]}</h4>
                <p className="apple-asset-item-date">
                  Updated {formatDate(deck.Properties["Last-Modified"]._text)}
                </p>
                <a
                  href={deck.Url._text}
                  target="_blank"
                  rel="noreferrer"
                  className="apple-asset-download-pill"
                >
                  <Download size={15} />
                  <span>Download Deck</span>
                </a>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="apple-assets-loading">
          <Sparkles className="spin-icon" size={24} color="#F05742" />
          <span>Loading decks...</span>
        </div>
      )}
    </div>
  );
};

export default Assets;
